import { useState, useCallback, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    AcademicCapIcon,
    MapPinIcon,
    CheckBadgeIcon,
    ArrowRightIcon,
    StarIcon,
    UsersIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    CurrencyRupeeIcon,
    XMarkIcon,
    AdjustmentsHorizontalIcon,
    SparklesIcon,
    ComputerDesktopIcon,
    UserGroupIcon,
    BanknotesIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { universityAPI, approvalAPI, degreeAPI, specializationAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import EnquiryModal from '../../components/Common/EnquiryModal'
import BannerSlider from '../../components/Universities/BannerSlider'

const Universities = () => {
    const [filters, setFilters] = useState({
        search: '',
        level: '',
        degrees: [],
        specializations: [],
        approvals: [],
        feeRange: '',
        page: 1,
        limit: 12,
        sort: 'order'
    })
    const [searchParams] = useSearchParams()

    // Sync filters with URL params
    useEffect(() => {
        const level = searchParams.get('level') || ''
        const degrees = searchParams.get('degrees') ? searchParams.get('degrees').split(',') : []
        const specializations = searchParams.get('specializations') ? searchParams.get('specializations').split(',') : []
        const approvals = searchParams.get('approvals') ? searchParams.get('approvals').split(',') : []
        const feeRange = searchParams.get('feeRange') || ''
        const search = searchParams.get('search') || ''

        setFilters(prev => ({
            ...prev,
            level,
            degrees,
            specializations,
            approvals,
            feeRange,
            search,
            page: 1
        }))
    }, [searchParams])

    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [enquiryModal, setEnquiryModal] = useState({
        isOpen: false,
        university: null,
        type: ''
    })

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['universities', filters],
        queryFn: () => {
            // Convert arrays to comma-separated strings for API
            const apiFilters = {
                ...filters,
                degrees: filters.degrees.length > 0 ? filters.degrees.join(',') : undefined,
                specializations: filters.specializations.length > 0 ? filters.specializations.join(',') : undefined,
                approvals: filters.approvals.length > 0 ? filters.approvals.join(',') : undefined,
            };
            // Remove empty arrays
            Object.keys(apiFilters).forEach(key => {
                if (apiFilters[key] === undefined || apiFilters[key] === '') {
                    delete apiFilters[key];
                }
            });
            return universityAPI.getUniversities(apiFilters);
        },
        keepPreviousData: false,
    })

    // Fetch master data for filters
    const { data: degreesData, isLoading: degreesLoading, error: degreesError } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees()
    })

    // Fetch specializations based on selected degrees
    const { data: specializationsData, isLoading: specializationsLoading, error: specializationsError } = useQuery({
        queryKey: ['specializations', filters.degrees],
        queryFn: () => {
            if (filters.degrees.length > 0) {
                // Fetch specializations for selected degrees
                return Promise.all(
                    filters.degrees.map(degreeId =>
                        specializationAPI.getSpecializations({ degree: degreeId })
                    )
                ).then(results => {
                    // Flatten and deduplicate specializations
                    const allSpecializations = results.flatMap(result => result.data?.specializations || []);
                    const uniqueSpecializations = allSpecializations.filter((spec, index, self) =>
                        index === self.findIndex(s => s._id === spec._id)
                    );
                    return { data: { specializations: uniqueSpecializations } };
                });
            } else {
                // Fetch all specializations when no degrees selected
                return specializationAPI.getSpecializations();
            }
        },
        enabled: true
    })

    const { data: approvalsData, isLoading: approvalsLoading, error: approvalsError } = useQuery({
        queryKey: ['approvals'],
        queryFn: () => approvalAPI.getApprovals()
    })

    const degrees = (degreesData?.data?.degrees || []).filter(degree =>
        !filters.level || degree.level === filters.level
    )
    const specializations = specializationsData?.data?.specializations || []
    const approvals = approvalsData?.data?.approvals || []


    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [key]: value,
                page: 1 // Reset to first page when filters change
            };

            // If level changes, reset degrees and specializations
            if (key === 'level') {
                newFilters.degrees = [];
                newFilters.specializations = [];
            }

            return newFilters;
        })
    }


    const handleMultiSelectFilter = (key, value, checked) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [key]: checked
                    ? [...prev[key], value]
                    : prev[key].filter(item => item !== value),
                page: 1
            };

            // If degrees are being changed, clear specializations
            if (key === 'degrees') {
                newFilters.specializations = [];
            }

            return newFilters;
        })
    }

    const clearAllFilters = () => {
        setFilters({
            search: '',
            level: '',
            degrees: [],
            specializations: [],
            approvals: [],
            feeRange: '',
            page: 1,
            limit: 12,
            sort: 'order'
        })
    }

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }))
    }

    const handleEnquiry = (university, type) => {
        setEnquiryModal({
            isOpen: true,
            university,
            type
        })
    }

    const handleCloseEnquiryModal = () => {
        setEnquiryModal({
            isOpen: false,
            university: null,
            type: ''
        })
    }

    // University Image Component with banner and logo overlay
    const UniversityImage = ({ university }) => {
        const [logoError, setLogoError] = useState(false)

        const handleLogoError = useCallback(() => {
            setLogoError(true)
        }, [])

        // Use the first banner image as the card background
        const bannerUrl = university.banner?.[0]?.url;

        return (
            <div className="relative w-full h-full overflow-hidden group">
                {/* Main Banner Image */}
                <div className="w-full h-full bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center transition-transform duration-700 group-hover:scale-105">
                    {bannerUrl ? (
                        <img
                            src={bannerUrl}
                            alt={university.name}
                            className="w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-90"
                        />
                    ) : (
                        <div className="flex flex-col items-center">
                            <AcademicCapIcon className="w-16 h-16 text-white/20 mb-2" />
                            <span className="text-white/40 text-[10px] font-bold uppercase tracking-tighter">Institution</span>
                        </div>
                    )}
                </div>

                {/* Darker overlay on hover for better text readability and depth */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 z-[1]"></div>

                {/* Glassy Small Logo Overlay - Positioned more elegantly */}
                {university.logo?.url && !logoError && (
                    <div className="absolute bottom-3 left-3 w-14 h-14 bg-white/95 p-2 rounded-2xl shadow-2xl border border-white/20 backdrop-blur-md z-10 flex items-center justify-center transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:rotate-3">
                        <img
                            src={university.logo.url}
                            alt={`${university.name} logo`}
                            className="max-w-full max-h-full object-contain rounded-lg filter drop-shadow-sm"
                            onError={handleLogoError}
                        />
                    </div>
                )}
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load universities" onRetry={refetch} />
            </div>
        )
    }

    const universities = data?.data?.universities || []
    const pagination = data?.data?.pagination || {}

    return (
        <>
            <Helmet>
                <title>Universities & Colleges | Margdarshak</title>
                <meta name="description" content="Browse top universities and colleges offering online and distance learning programs. Find UGC recognized institutions with quality education." />
                <meta name="keywords" content="universities, colleges, online education, distance learning, UGC recognized, higher education" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-0">
                {/* Premium Light Theme Universities Hero Section - Refined */}
                <section className="relative pt-28 lg:pt-32 pb-10 lg:pb-16 overflow-hidden bg-white border-b border-gray-100">
                    {/* Dynamic Mesh Gradient Background - Subtle for Light Theme */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-primary-100 blur-[120px] animate-pulse"></div>
                        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-100 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                        <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] rounded-full bg-accent-50 blur-[100px]"></div>
                    </div>

                    {/* Subtle Overlay Grid */}
                    <div className="absolute inset-0 z-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

                    {/* Floating Badges - Eye Catching */}
                    <div className="absolute inset-0 z-10 pointer-events-none hidden lg:block">
                        <motion.div 
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute top-40 left-[10%] bg-white/80 backdrop-blur-md border border-primary-200 px-4 py-2 rounded-2xl shadow-xl flex items-center space-x-2"
                        >
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                                <AcademicCapIcon className="w-5 h-5 text-primary-600" />
                            </div>
                            <span className="text-sm font-black text-slate-800 tracking-tight">UGC Recognized</span>
                        </motion.div>

                        <motion.div 
                            animate={{ y: [0, 10, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="absolute top-60 right-[15%] bg-white/80 backdrop-blur-md border border-indigo-200 px-4 py-2 rounded-2xl shadow-xl flex items-center space-x-2"
                        >
                            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                                <SparklesIcon className="w-5 h-5 text-indigo-600" />
                            </div>
                            <span className="text-sm font-black text-slate-800 tracking-tight">NAAC A+ Accredited</span>
                        </motion.div>
                    </div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-5xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center space-x-2 bg-primary-50 border border-primary-100 px-4 py-1 rounded-full mb-6"
                            >
                                <span className="flex h-2 w-2 rounded-full bg-primary-600"></span>
                                <span className="text-xs font-black text-primary-700 uppercase tracking-widest">Global University Network</span>
                            </motion.div>

                            <motion.h1 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-4xl sm:text-6xl lg:text-7xl font-black mb-6 text-slate-900 leading-[1.1] tracking-tight"
                            >
                                Discover Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-indigo-600 to-accent-600">Dream Career</span> at Top Rated Universities
                            </motion.h1>

                            <motion.p 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-base sm:text-lg text-slate-600 mb-8 max-w-3xl mx-auto font-medium leading-relaxed"
                            >
                                We partner with leading institutions to bring you UGC/DEB recognized online & distance programs. Quality education, now at your fingertips.
                            </motion.p>

                            {/* Value Features Grid */}
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8 border-t border-slate-100 pt-8"
                            >
                                {[
                                    { label: "UGC Recognized", icon: CheckBadgeIcon, color: "text-blue-600" },
                                    { label: "100% Online", icon: ComputerDesktopIcon, color: "text-indigo-600" },
                                    { label: "Expert Support", icon: UserGroupIcon, color: "text-accent-600" },
                                    { label: "Emi Options", icon: BanknotesIcon, color: "text-emerald-600" }
                                ].map((feature, idx) => (
                                    <div key={idx} className="flex items-center space-x-3 justify-center md:justify-start">
                                        <feature.icon className={`w-5 h-5 ${feature.color}`} />
                                        <span className="text-sm font-bold text-slate-700">{feature.label}</span>
                                    </div>
                                ))}
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="flex items-center justify-center space-x-4 opacity-70"
                            >
                                <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Trusted by 50+ Global Institutions</span>
                                <div className="h-px w-12 bg-slate-200"></div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Results Section */}
                <section className="py-12">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Banners Slider at top of list */}
                        <BannerSlider />
                        <div className="flex flex-col lg:flex-row gap-8">
                            {/* Sidebar Filters */}
                            <div className={`lg:w-80 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}>
                                <div className="bg-white rounded-xl shadow-soft p-6 sticky top-24">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={clearAllFilters}
                                                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                                            >
                                                Clear All
                                            </button>
                                            <button
                                                onClick={() => setSidebarOpen(false)}
                                                className="lg:hidden p-1 hover:bg-gray-100 rounded"
                                            >
                                                <XMarkIcon className="w-5 h-5 text-gray-500" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Level Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Level</label>
                                        <select
                                            value={filters.level}
                                            onChange={(e) => handleFilterChange('level', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        >
                                            <option value="">All Levels</option>
                                            <option value="Undergraduate (UG)">Undergraduate</option>
                                            <option value="Postgraduate (PG)">Postgraduate</option>
                                            <option value="Diploma">Diploma</option>
                                            <option value="Certificate">Certificate</option>
                                        </select>
                                    </div>

                                    {/* Degrees Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Degrees</label>
                                        {degreesLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : degreesError ? (
                                            <div className="text-red-500 text-sm">Failed to load degrees</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {degrees.map((degree) => (
                                                    <label key={degree._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.degrees.includes(degree._id)}
                                                            onChange={(e) => handleMultiSelectFilter('degrees', degree._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{degree.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Specializations Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Specializations</label>
                                        {specializationsLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : specializationsError ? (
                                            <div className="text-red-500 text-sm">Failed to load specializations</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {specializations.map((specialization) => (
                                                    <label key={specialization._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.specializations.includes(specialization._id)}
                                                            onChange={(e) => handleMultiSelectFilter('specializations', specialization._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{specialization.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Fee Range Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Fee Range</label>
                                        <select
                                            value={filters.feeRange}
                                            onChange={(e) => handleFilterChange('feeRange', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        >
                                            <option value="">All Fee Ranges</option>
                                            <option value="below_2_lakhs">Below ₹2 Lakhs</option>
                                            <option value="above_2_lakhs">Above ₹2 Lakhs</option>
                                        </select>
                                    </div>


                                    {/* Approvals Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Approvals</label>
                                        {approvalsLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : approvalsError ? (
                                            <div className="text-red-500 text-sm">Failed to load approvals</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {approvals.map((approval) => (
                                                    <label key={approval._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.approvals.includes(approval._id)}
                                                            onChange={(e) => handleMultiSelectFilter('approvals', approval._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{approval.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="flex-1">
                                {isLoading ? (
                                    <div className="flex justify-center py-12">
                                        <LoadingSpinner size="lg" />
                                    </div>
                                ) : (
                                    <>
                                        {/* Results Count */}
                                        <div className="mb-8">
                                            {universities.length > 0 ? (
                                                <p className="text-gray-600">
                                                    Showing {universities.length} of {pagination.total_count || 0} universities
                                                </p>
                                            ) : (
                                                <div className="text-center py-12">
                                                    <div className="text-gray-500 mb-4">
                                                        <AcademicCapIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No universities found</h3>
                                                        <p className="text-gray-600 mb-4">
                                                            Try adjusting your filters or search terms to find more results.
                                                        </p>
                                                        <button
                                                            onClick={clearAllFilters}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                                        >
                                                            Clear All Filters
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Universities Grid */}
                                        {universities.length > 0 && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                                                {universities.map((university, index) => (
                                                    <motion.div
                                                        key={university._id}
                                                        initial={{ opacity: 0, y: 30 }}
                                                        whileInView={{ opacity: 1, y: 0 }}
                                                        viewport={{ once: true, margin: "-50px" }}
                                                        transition={{ duration: 0.6, delay: (index % 3) * 0.1, ease: "easeOut" }}
                                                        className="bg-white rounded-[2rem] shadow-xl hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)] border border-gray-100/50 hover:border-primary-200/50 transition-all duration-500 overflow-hidden flex flex-col h-full group"
                                                    >
                                                        {/* University Image Header - Increased height and refined overflow */}
                                                        <div className="relative h-56 overflow-hidden shrink-0">
                                                            <UniversityImage university={university} />

                                                            {/* Avg. Fee Badge - Replaces Featured Partner */}
                                                            <div className="absolute top-3 right-3 flex items-center space-x-1.5 bg-white/95 backdrop-blur-md border border-primary-100 text-primary-700 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-widest shadow-xl z-20">
                                                                <CurrencyRupeeIcon className="w-3 h-3" />
                                                                <span>Fee: {university.fee_range?.type === 'below_2_lakhs' ? 'Below 2L' :
                                                                    university.fee_range?.type === 'above_2_lakhs' ? 'Above 2L' :
                                                                        university.fee_range?.type || 'Contact'}</span>
                                                            </div>
                                                        </div>

                                                        {/* Content - Refined spacing and typography */}
                                                        <div className="p-4 flex flex-col flex-1">
                                                            {/* Title - Larger and more prominent */}
                                                            <div className="mb-4">
                                                                <Link to={`/universities/${university.slug}`} className="block">
                                                                    <h3 className="text-xl md:text-2xl font-black text-gray-900 leading-tight group-hover:text-primary-600 transition-colors duration-300">
                                                                        {university.name}
                                                                    </h3>
                                                                </Link>
                                                            </div>

                                                            {/* Introduction / Summary - Better truncation and line-height */}
                                                            {university.introduction && (
                                                                <p className="text-gray-500 text-sm line-clamp-2 mb-6 leading-relaxed font-medium">
                                                                    {university.introduction.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                                </p>
                                                            )}

                                                            {/* Approval Tags - Small badges */}
                                                            {university.approvals && university.approvals.length > 0 && (
                                                                <div className="">
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {university.approvals.slice(0, 3).map((approval) => (
                                                                            <span
                                                                                key={approval._id || approval}
                                                                                className="inline-flex items-center bg-primary-50 text-primary-700 px-2.5 py-1 rounded-full text-[10px] font-bold border border-primary-100/50"
                                                                            >
                                                                                <CheckBadgeIcon className="w-3 h-3 mr-1" />
                                                                                <span className="truncate max-w-[100px]">{approval.name || approval}</span>
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Spacer */}
                                                            <div className="flex-1"></div>

                                                            {/* Fee & Action Block - Modernized and integrated */}
                                                            <div className="space-y-4">
                                                                <div className="h-2"></div>

                                                                {/* Primary Action Buttons */}
                                                                <div className="flex gap-2.5">
                                                                    <button
                                                                        onClick={() => handleEnquiry(university, 'enquiry')}
                                                                        className="flex-[2] bg-primary-600 text-white px-6 py-3.5 rounded-2xl hover:bg-primary-700 transition-all font-bold text-sm shadow-lg shadow-primary-200 active:scale-[0.98] flex items-center justify-center space-x-2"
                                                                    >
                                                                        <span>Enquiry Now</span>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleEnquiry(university, 'brochure')}
                                                                        className="flex-1 bg-white text-gray-700 border border-gray-200 px-4 py-3.5 rounded-2xl hover:bg-gray-50 transition-all font-bold text-sm shadow-sm active:scale-[0.98] flex justify-center items-center"
                                                                        title="Brochure"
                                                                    >
                                                                        Brochure
                                                                    </button>
                                                                    <Link
                                                                        to={`/universities/${university.slug}`}
                                                                        className="flex-shrink-0 bg-primary-50 text-primary-600 border border-primary-100 w-12 py-3.5 rounded-2xl hover:bg-primary-600 hover:text-white transition-all duration-300 shadow-sm flex justify-center items-center"
                                                                    >
                                                                        <ArrowRightIcon className="w-5 h-5" />
                                                                    </Link>
                                                                </div>

                                                                {/* Secondary Action - Using brand orange/accent instead of black */}
                                                                <button
                                                                    onClick={() => handleEnquiry(university, 'counselling')}
                                                                    className="w-full bg-accent-600 text-white px-6 py-4 rounded-2xl hover:bg-accent-700 transition-all duration-300 font-black text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-accent-200 active:scale-[0.98]"
                                                                >
                                                                    Free Career Counselling
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Pagination */}
                                        {pagination.total_pages > 1 && (
                                            <div className="mt-12 flex justify-center">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                                        disabled={pagination.current_page === 1}
                                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Previous
                                                    </button>

                                                    {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={`px-4 py-2 text-sm font-medium rounded-lg ${page === pagination.current_page
                                                                ? 'bg-primary-600 text-white'
                                                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}

                                                    <button
                                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                                        disabled={pagination.current_page === pagination.total_pages}
                                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Enquiry Modal */}
            <EnquiryModal
                isOpen={enquiryModal.isOpen}
                onClose={handleCloseEnquiryModal}
                title={
                    enquiryModal.type === 'enquiry' ? 'Quick Enquiry' :
                        enquiryModal.type === 'brochure' ? 'Download Brochure' :
                            'Free Career Counselling'
                }
                source={enquiryModal.type}
                itemName={enquiryModal.university?.name || ''}
                itemType="university"
                itemId={enquiryModal.university?._id || null}
            />
        </>
    )
}

export default Universities
